import type { EtapaDaPiora } from "@base/contracts";
import { EstadoVazio, cn } from "@base/ui";
import { formatar } from "../../dashboard";

/** `parte` 0..1 vira largura 0..100, limitada. */
export function percentualDaParte(parte: number): number {
  if (!Number.isFinite(parte)) return 0;
  return Math.min(100, Math.max(0, parte * 100));
}

const CABECA = "border-b border-border px-3 py-2.5 text-right text-xs font-semibold text-muted-foreground";
const CELULA = "border-b border-border/60 p-3 text-right tabular-nums";

/** Tabela da piora: base, atual, variação e a parte em barra. A etapa que mais pesou fica em destaque. */
export function BarrasDaPiora({ etapas }: { readonly etapas: readonly EtapaDaPiora[] }) {
  if (etapas.length === 0) {
    return <EstadoVazio titulo="Sem etapas de piora." descricao="Nenhuma etapa explica a variação deste período." />;
  }
  const maior = Math.max(...etapas.map((etapa) => etapa.parte));
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">De onde veio a piora</caption>
        <thead>
          <tr>
            <th scope="col" className={cn(CABECA, "pl-0 text-left")}>Etapa</th>
            <th scope="col" className={CABECA}>Base</th>
            <th scope="col" className={CABECA}>Atual</th>
            <th scope="col" className={CABECA}>Variação</th>
            <th scope="col" className={cn(CABECA, "w-[34%] pl-4 pr-0 text-left")}>Parte da piora</th>
          </tr>
        </thead>
        <tbody>
          {etapas.map((etapa) => {
            const largura = percentualDaParte(etapa.parte);
            const principal = maior > 0 && etapa.parte === maior;
            return (
              <tr
                key={`${etapa.etapa}|${etapa.atualRotulo}|${etapa.parte}`}
                className={cn("[&:last-child>td]:border-b-0", principal && "bg-destructive/5")}
              >
                <th scope="row" className={cn(CELULA, "pl-0 text-left", principal ? "font-bold" : "font-normal")}>
                  {etapa.etapa}
                </th>
                <td className={CELULA}>{etapa.baseRotulo}</td>
                <td className={cn(CELULA, principal && "font-bold")}>{etapa.atualRotulo}</td>
                <td className={cn(CELULA, principal && "font-bold text-destructive")}>{etapa.variacaoRotulo}</td>
                <td className={cn(CELULA, "pl-4 pr-0")}>
                  <div className="flex items-center gap-2.5">
                    <div
                      role="meter"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(largura)}
                      aria-label={`${etapa.etapa}: ${formatar.porcentagem(etapa.parte)} da piora`}
                      className="h-2 flex-1 overflow-hidden rounded-full bg-muted"
                    >
                      <div
                        className={cn(
                          "h-full rounded-full motion-safe:transition-[width] motion-safe:duration-300",
                          principal ? "bg-destructive" : "bg-muted-foreground/60",
                        )}
                        style={{ width: `${largura}%` }}
                      />
                    </div>
                    <span className={cn("w-12 text-right", principal ? "font-bold text-destructive" : "text-muted-foreground")}>
                      {formatar.porcentagem(etapa.parte)}
                    </span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
